import type { DeepPartial } from 'react-hook-form';

import type { EventFormComplete } from '../../../forms/event/EventForm/schema.ts';
import { notEmpty } from '../../../lib/notEmpty.ts';
import { removeUndefinedValues } from '../../../lib/removeUndefinedValues.ts';
import { mapAdditionalDetailsToEventDetails } from '../../../mappers/incompleteEvent/formToApi/lib/mapAdditionalDetailsToEventDetails.ts';
import { mapTagsToTargetAudiences } from '../../../mappers/incompleteEvent/formToApi/lib/mapTagsToTargetAudiences.ts';
import { parseIsoDate } from '../../../mappers/incompleteEvent/formToApi/lib/parseIsoDate.ts';
import { parseOnlineLink } from '../../../mappers/incompleteEvent/formToApi/lib/parseOnlineLink.ts';
import { parseVenue } from '../../../mappers/incompleteEvent/formToApi/lib/parseVenue.ts';
import { parseFormValue } from '../../../mappers/lib/parseFormValue.ts';
import type { EventApiUpdateData } from './types.ts';

export const eventEditFormToEventApi = (eventEditFormValues: DeepPartial<EventFormComplete>): EventApiUpdateData => {
  const target_audiences = eventEditFormValues.tags
    ? mapTagsToTargetAudiences(eventEditFormValues.tags.filter(notEmpty))
    : undefined;

  const event_details = eventEditFormValues.eventDetails
    ? mapAdditionalDetailsToEventDetails(eventEditFormValues.eventDetails.filter(notEmpty))
    : undefined;

  const type = parseFormValue(eventEditFormValues.eventType);
  // Online and secret events have no venue. It must be cleared with `null`; `0` is not a valid
  // foreign key and made Directus reject the whole update with a 400, so such events could not
  // be saved at all. `undefined` is no alternative here — removeUndefinedValues would drop the
  // key and leave the previous venue in place.
  const venue =
    type === 'online' || type === 'secret' ? null : parseVenue(type ?? undefined, eventEditFormValues.venue);

  const data = {
    name: parseFormValue(eventEditFormValues.name) ?? undefined,
    category: eventEditFormValues.category ? Number.parseInt(eventEditFormValues.category, 10) : undefined,
    target_audiences,
    event_details,
    long_description: eventEditFormValues.description ?? {},
    type,
    venue,
    online_link: parseOnlineLink(eventEditFormValues.eventType, eventEditFormValues.onlineLink),
    start: parseIsoDate(eventEditFormValues.start),
    end: parseIsoDate(eventEditFormValues.end),
    image: parseFormValue(eventEditFormValues.image),
    image_original: parseFormValue(eventEditFormValues.image_original),
    image_crop_data: eventEditFormValues.image_crop_data ?? null,
    ticket_sale_url: parseFormValue(eventEditFormValues.ticketshopLink),
    website_link: parseFormValue(eventEditFormValues.websiteLink),
    instagram_link: parseFormValue(eventEditFormValues.instagramLink),
    tiktok_link: parseFormValue(eventEditFormValues.tiktokLink),
    spotify_link: parseFormValue(eventEditFormValues.spotifyLink),
  };

  return removeUndefinedValues(data) as EventApiUpdateData;
};
